/**
 * Drag & Drop Types
 *
 * Unified drag & drop system for sidebar items.
 * All sidebar items can be dragged to the main content area
 * to open them in split view.
 */

import { SplitPanelType } from "@/hooks/use-tabs-store";

// ============================================================================
// Drag Type Constants
// ============================================================================

/**
 * MIME types for drag & drop operations.
 * Each type identifies what kind of item is being dragged.
 */
export const DRAG_TYPES = {
  /** Generic sidebar item (can be any type) */
  SIDEBAR_ITEM: "application/x-crowlingo-sidebar-item",
  /** Dashboard item */
  DASHBOARD: "application/x-crowlingo-dashboard",
  /** Tracker item */
  TRACKER: "application/x-crowlingo-tracker",
  /** Alert item */
  ALERT: "application/x-crowlingo-alert",
  /** Enrichment item */
  ENRICHMENT: "application/x-crowlingo-enrichment",
  /** Chat/conversation item */
  CHAT: "application/x-crowlingo-chat",
  /** Widget item */
  WIDGET: "application/x-crowlingo-widget",
  /** Investigation item */
  INVESTIGATION: "application/x-crowlingo-investigation",
  /** Report item */
  REPORT: "application/x-crowlingo-report",
} as const;

export type DragType = (typeof DRAG_TYPES)[keyof typeof DRAG_TYPES];

// ============================================================================
// Drag Payload
// ============================================================================

/**
 * Payload structure for drag & drop operations.
 * This is serialized and attached to the drag event.
 */
export interface DragPayload {
  /** Type of the dragged item */
  type: SplitPanelType;
  /** Unique ID of the resource */
  id: string;
  /** Route to navigate to when opening this item */
  route: string;
  /** Display title for the panel */
  title: string;
  /** Optional icon identifier */
  icon?: string;
  /** Optional color for theming */
  color?: string;
}

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Set drag data on a drag event.
 * Attaches the payload as JSON to multiple MIME types for compatibility.
 */
export function setDragPayload(
  event: React.DragEvent,
  payload: DragPayload,
): void {
  const jsonData = JSON.stringify(payload);

  // Set generic sidebar item type (always)
  event.dataTransfer.setData(DRAG_TYPES.SIDEBAR_ITEM, jsonData);

  // Set specific type based on payload type
  switch (payload.type) {
    case "dashboard":
      event.dataTransfer.setData(DRAG_TYPES.DASHBOARD, jsonData);
      break;
    case "tracker":
    case "trackers":
      event.dataTransfer.setData(DRAG_TYPES.TRACKER, jsonData);
      break;
    case "alert":
    case "alerts":
      event.dataTransfer.setData(DRAG_TYPES.ALERT, jsonData);
      break;
    case "enrichment":
    case "enrichments":
      event.dataTransfer.setData(DRAG_TYPES.ENRICHMENT, jsonData);
      break;
    case "chat":
      event.dataTransfer.setData(DRAG_TYPES.CHAT, jsonData);
      break;
    case "widgets":
      event.dataTransfer.setData(DRAG_TYPES.WIDGET, jsonData);
      break;
    case "investigation":
    case "investigations":
      event.dataTransfer.setData(DRAG_TYPES.INVESTIGATION, jsonData);
      break;
    case "reports":
      event.dataTransfer.setData(DRAG_TYPES.REPORT, jsonData);
      break;
  }

  // Set drag effect - use "move" for internal sidebar operations
  event.dataTransfer.effectAllowed = "move";
}

/**
 * Get drag payload from a drag event.
 * Attempts to parse the payload from the event's data transfer.
 */
export function getDragPayload(event: React.DragEvent): DragPayload | null {
  // Try generic sidebar item type first
  let data = event.dataTransfer.getData(DRAG_TYPES.SIDEBAR_ITEM);

  // Fallback to specific types
  if (!data) {
    for (const type of Object.values(DRAG_TYPES)) {
      data = event.dataTransfer.getData(type);
      if (data) break;
    }
  }

  if (!data) {
    return null;
  }

  try {
    return JSON.parse(data) as DragPayload;
  } catch {
    return null;
  }
}

/**
 * Check if a drag event contains a sidebar item.
 */
export function isSidebarItemDrag(event: DragEvent | React.DragEvent): boolean {
  const types = Array.from(event.dataTransfer?.types || []);
  return types.some((type) =>
    Object.values(DRAG_TYPES).includes(type as DragType),
  );
}

/**
 * Get the specific drag type from an event.
 */
export function getDragType(
  event: DragEvent | React.DragEvent,
): DragType | null {
  const types = Array.from(event.dataTransfer?.types || []);

  for (const dragType of Object.values(DRAG_TYPES)) {
    if (types.includes(dragType)) {
      return dragType;
    }
  }

  return null;
}

// ============================================================================
// Route Builders
// ============================================================================

/**
 * Build a route for a given item type and ID.
 */
export function buildRouteForItem(type: SplitPanelType, id?: string): string {
  switch (type) {
    case "dashboard":
      return id ? `/dashboards?id=${id}` : "/dashboards";
    case "tracker":
      return id ? `/trackers?id=${id}` : "/trackers";
    case "trackers":
      return "/trackers";
    case "alert":
      return id ? `/alerts?id=${id}` : "/alerts";
    case "alerts":
      return "/alerts";
    case "enrichment":
      return id ? `/enrichments?id=${id}` : "/enrichments";
    case "enrichments":
      return "/enrichments";
    case "chat":
      return id ? `/chat?id=${id}` : "/chat";
    case "search":
      return "/search";
    case "home":
      return "/";
    case "account":
      return "/account";
    case "widgets":
      return "/widgets";
    case "branches":
      return "/branches";
    case "investigation":
      return id ? `/investigations?id=${id}` : "/investigations";
    case "investigations":
      return "/investigations";
    case "reports":
      return "/reports";
    case "workspaces":
      return "/workspaces";
    case "tokens":
      return "/tokens";
    case "members":
      return "/members";
    case "channels":
      return "/channels";
    case "organization":
      return "/organization";
    case "settings":
      return "/settings";
    default:
      return "/";
  }
}

/**
 * Create a drag payload for a dashboard.
 */
export function createDashboardDragPayload(
  id: string,
  title: string,
  color?: string,
): DragPayload {
  return {
    type: "dashboard",
    id,
    route: buildRouteForItem("dashboard", id),
    title,
    color,
  };
}

/**
 * Create a drag payload for a tracker.
 */
export function createTrackerDragPayload(
  id: string,
  title: string,
): DragPayload {
  return {
    type: "tracker",
    id,
    route: buildRouteForItem("tracker", id),
    title,
  };
}

/**
 * Create a drag payload for an alert.
 */
export function createAlertDragPayload(id: string, title: string): DragPayload {
  return {
    type: "alert",
    id,
    route: buildRouteForItem("alert", id),
    title,
  };
}

/**
 * Create a drag payload for an enrichment.
 */
export function createEnrichmentDragPayload(
  id: string,
  title: string,
): DragPayload {
  return {
    type: "enrichment",
    id,
    route: buildRouteForItem("enrichment", id),
    title,
  };
}

/**
 * Create a drag payload for a widget.
 */
export function createWidgetDragPayload(
  id: string,
  title: string,
  dashboardId?: string,
): DragPayload {
  return {
    type: "widgets",
    id,
    route: dashboardId
      ? `/dashboards?id=${dashboardId}&widget=${id}`
      : `/widgets?id=${id}`,
    title,
  };
}

/**
 * Create a drag payload for an investigation.
 */
export function createInvestigationDragPayload(
  id: string,
  title: string,
): DragPayload {
  return {
    type: "investigation",
    id,
    route: `/investigations/${id}`,
    title,
  };
}

/**
 * Create a drag payload for a conversation (chat).
 */
export function createChatDragPayload(id: string, title: string): DragPayload {
  return {
    type: "chat",
    id,
    route: `/chat?id=${id}`,
    title,
  };
}

/**
 * Create a drag payload for a report.
 */
export function createReportDragPayload(
  id: string,
  title: string,
): DragPayload {
  return {
    type: "reports",
    id,
    route: `/reports?id=${id}`,
    title,
  };
}

/**
 * Create a drag payload for an app/page (from app favorites).
 * Used for sidebar app icons like Trackers, Dashboards, Alerts, etc.
 */
export function createAppDragPayload(
  appId: string,
  title: string,
  route: string,
): DragPayload {
  // Map app ID to split panel type
  const typeMap: Record<string, SplitPanelType> = {
    trackers: "trackers",
    dashboards: "dashboard",
    alerts: "alerts",
    playground: "search",
    chat: "chat",
    enrichments: "enrichments",
    widgets: "widgets",
    branches: "branches",
    investigations: "investigations",
    reports: "reports",
    workspaces: "workspaces",
    organization: "organization",
    members: "members",
    api_tokens: "tokens",
    channels: "channels",
    settings: "settings",
    home: "home",
    account: "account",
  };

  const type = typeMap[appId] || "home";

  return {
    type,
    id: appId,
    route,
    title,
  };
}
